import { BEIGE_300, BEIGE_500, GRAY_700, GRAY_900, MOBILE_VIEW_WIDTH_QUERY, RhetorikFontFamily } from 'constants/styling/theme'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { FC, ReactNode, useCallback } from 'react'
import { WorkspaceType, useSignupRegistration } from '../SignupRegistration.context'

import { AddressAutocomplete, AddressFields } from './AddressAutocomplete'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import BusinessIcon from '@mui/icons-material/Business'
import { Country } from 'constants/country'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { OwnerFilterTab } from 'components/common/OwnerFilterTab'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { PhoneInput } from 'components/common/PhoneInput'
import { QueryStatus } from 'components/common/QueryStatus'
import { useDynamicFlowController } from 'components/common/DynamicFlow'
import { useTranslation } from 'react-i18next'

const CustomPhoneInput: FC<{ children: ReactNode }> = ({ children }) => (
  <Box width="100%" sx={{
    'input': {
      height: '4rem',
      width: '100%',
      borderRadius: '.8rem',
      border: 'unset !important',
    },
    '.iti': {
      display: 'block',
    }
  }}>
    {children}
  </Box>
)

export const SignupCreateWorkspace: React.FC = () => {
  const { t } = useTranslation(['signup', 'country', 'language', 'common'])
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const flowCtrl = useDynamicFlowController()

  const {
    workspaceType,
    setWorkspaceType,
    organizationName,
    setOrganizationName,
    addressStreet,
    setAddressStreet,
    addressZip,
    setAddressZip,
    addressCity,
    setAddressCity,
    addressCountry,
    setAddressCountry,
    isCreateWorkspaceStepValid,
    country,
    phone,
    setPhone,
    isPhoneValid,
    setIsPhoneValid,
    phoneErrorMsg,
    setPhoneErrorMsg,
    submitCreateUser,
    createUser,
  } = useSignupRegistration()

  const handlePlaceSelect = useCallback((fields: AddressFields) => {
    setAddressStreet(fields.street)
    setAddressZip(fields.zip)
    setAddressCity(fields.city)
    setAddressCountry(fields.country)
  }, [setAddressStreet, setAddressZip, setAddressCity, setAddressCountry])

  const onBackClick = useCallback(() => {
    const currentStep = flowCtrl.currentNode

    if (!currentStep?.prev) return

    flowCtrl.setHead(currentStep.prev)

    // Omit flowCtrl dependency to prevent loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowCtrl.currentNode?.prev, flowCtrl.head])

  return (
    <Stack padding={isMobileView ? '8rem 0' : '8rem'} width="90%" maxWidth="50rem">

      <Stack gap="1.6rem" alignItems="center" marginBottom="4rem">
        <Typography variant="h6" color={GRAY_900} fontWeight={500} fontFamily={RhetorikFontFamily}>{t('your_workspace')}</Typography>
        <Typography variant="text-sm" color={GRAY_700}>{t('select_workspace_kind')}</Typography>
      </Stack>

      <Stack flexDirection="row" padding=".4rem" borderRadius=".8rem" marginBottom="2.4rem" sx={{ background: BEIGE_500 }}>
        <OwnerFilterTab
          active={workspaceType === WorkspaceType.BUSINESS}
          icon={<BusinessIcon fontSize='inherit' />}
          text={t('business_account')}
          onClick={() => {
            if (!isPhoneValid) setPhone('')
            setWorkspaceType(WorkspaceType.BUSINESS)
          }}
        />
        <OwnerFilterTab
          active={workspaceType === WorkspaceType.PERSONAL}
          icon={<PersonOutlineIcon fontSize='inherit' />}
          text={t('personal_account')}
          onClick={() => {
            if (!isPhoneValid) setPhone('')
            setWorkspaceType(WorkspaceType.PERSONAL)
          }}
        />
      </Stack>

      {workspaceType === WorkspaceType.BUSINESS &&
        <Stack gap="1.6rem" marginBottom="4rem" className="fs-mask">

          <MUIInputField
            required
            label={t('organization_name')}
            value={organizationName}
            onChange={e => setOrganizationName(e.target.value)}
          />

          <AddressAutocomplete
            street={addressStreet}
            zip={addressZip}
            city={addressCity}
            country={addressCountry}
            onStreetChange={setAddressStreet}
            onZipChange={setAddressZip}
            onCityChange={setAddressCity}
            onCountryChange={setAddressCountry}
            onPlaceSelect={handlePlaceSelect}
          />

          <MUIInputField
            required
            label={t('business_phone')}
            errorText={phoneErrorMsg}
            isError={!isPhoneValid}
            customInput={
              <CustomPhoneInput>
                <PhoneInput
                  value={phone}
                  onChange={(value: string) => setPhone(value)}
                  onValidityChange={(isValid, errorMessage) => {
                    setPhoneErrorMsg(isValid ? '' : errorMessage)
                    setIsPhoneValid(isValid)
                  }}
                  initialCountry={country || Country.CH}
                />
              </CustomPhoneInput>
            }
          />

        </Stack>
      }

      {workspaceType === WorkspaceType.PERSONAL &&
        <Stack gap="1.6rem" marginBottom="4rem" className="fs-mask">

          <AddressAutocomplete
            street={addressStreet}
            zip={addressZip}
            city={addressCity}
            country={addressCountry}
            onStreetChange={setAddressStreet}
            onZipChange={setAddressZip}
            onCityChange={setAddressCity}
            onCountryChange={setAddressCountry}
            onPlaceSelect={handlePlaceSelect}
          />

          <MUIInputField
            required
            label={t('mobile_phone')}
            errorText={phoneErrorMsg}
            isError={!isPhoneValid}
            customInput={
              <CustomPhoneInput>
                <PhoneInput
                  value={phone}
                  onChange={(value: string) => setPhone(value)}
                  onValidityChange={(isValid, errorMessage) => {
                    setPhoneErrorMsg(isValid ? '' : errorMessage)
                    setIsPhoneValid(isValid)
                  }}
                  initialCountry={country || Country.CH}
                />
              </CustomPhoneInput>
            }
          />
        </Stack>
      }

      <QueryStatus
        query={createUser}
        showStates={['error', 'success']}
        spaceBottomRem={2}
      />

      {!isMobileView &&
        <Stack alignItems="center" justifyContent="flex-end" flexDirection="row" gap="1rem">
          <MUIButton
            size="lg"
            variant="text"
            type="secondaryNoBorder"
            onClick={onBackClick}
          >
            {t('back')}
          </MUIButton>

          <MUIButton
            size="lg"
            endIcon={<ArrowForwardRoundedIcon fontSize="large" />}
            disabled={!isCreateWorkspaceStepValid}
            isLoading={createUser.isPending}
            onClick={submitCreateUser}
          >
            {t('discover_bkbn')}
          </MUIButton>
        </Stack>
      }

      {isMobileView &&
        <Stack
          gap=".4rem"
          alignItems="center"
          justifyContent="flex-end"
          padding="2.4rem 1.6rem"
          flexDirection="row"
          position="fixed"
          bottom="0"
          left="0"
          width="100%"
          boxSizing="border-box"
          zIndex={1}
          sx={{
            background: BEIGE_300
          }}
        >
          <MUIButton
            size="lg"
            variant="text"
            type="secondaryNoBorder"
            onClick={onBackClick}
          >
            {t('back')}
          </MUIButton>

          <MUIButton
            fullWidth
            size="lg"
            endIcon={<ArrowForwardRoundedIcon fontSize="large" />}
            disabled={!isCreateWorkspaceStepValid}
            isLoading={createUser.isPending}
            onClick={submitCreateUser}
          >
            {t('discover_bkbn')}
          </MUIButton>
        </Stack>
      }

    </Stack>
  )
}
